import { useState } from "react";
import { useTranslation } from "react-i18next";

// Endonyms, short codes and the shared list live in one place — the template
// editor's authoring-language tabs use the very same array.
import { LANGUAGES } from "../../lang/languages";
import { confirmLeaveUnsavedWork } from "../../utils/helpers/unsavedWork";

/**
 * Three-way segmented control, not a dropdown.
 *
 * With exactly three options a dropdown costs two interactions to do anything
 * and hides the alternatives until opened — and for a user who has landed in a
 * language they cannot read, "find the control, guess that it opens, read the
 * options" is the worst possible flow. Laying all three out shows the current
 * language and both escape routes at a glance, in one click.
 *
 * It also removes an entire class of bug. The dropdown this replaces nested an
 * interactive <button> inside a role="option", which ARIA forbids — screen
 * readers flattened it and aria-selected was unreliable — and it needed
 * hand-rolled arrow-key handling, focus return and outside-click detection to
 * be usable. Three plain buttons in a labelled group need none of that: Tab and
 * Enter already work, and the browser owns focus.
 *
 * Styling mirrors the School/Teacher toggle in BaseLayout so the header reads
 * as one control family rather than two unrelated widgets.
 */
export default function LanguageSelector() {
  const { t, i18n } = useTranslation();
  // Which language is mid-switch. Guards against a second click landing while
  // changeLanguage is still resolving and the remount has not yet happened.
  const [pending, setPending] = useState(null);

  // `resolvedLanguage` is what i18next actually settled on; `language` can hold
  // an unsupported or region-tagged value that matches nothing in LANGUAGES.
  const active = i18n.resolvedLanguage || "en";

  const select = async (id) => {
    if (id === active || pending) return;
    // The switch remounts the routed tree (App.js keys <Routes> on the
    // language), which silently discards whatever the current page holds in
    // state — a half-answered observation, an unsaved template draft. The
    // remount is not a document unload, so the browser's own prompt never
    // fires; ask on the page's behalf instead.
    if (!confirmLeaveUnsavedWork(t("common.unsavedLeaveConfirm"))) return;
    setPending(id);
    try {
      // No window.location.reload() here. Server-owned content still has to be
      // re-requested under the new Accept-Language, but App.js keys the routed
      // tree on the active language, so this triggers a REMOUNT and every
      // page's fetch effect runs again — same guarantee, without the white
      // flash or re-downloading the bundle. i18n.js additionally clears the one
      // store that caches translated server data.
      await i18n.changeLanguage(id);
    } finally {
      setPending(null);
    }
  };

  return (
    <div
      role="group"
      // Without this the group is an unnamed row of three-letter codes; a
      // screen reader user has no way to know it selects a language.
      aria-label={t("common.language")}
      className="inline-flex items-center gap-0.5 rounded-lg bg-slate-100 p-1"
    >
      {LANGUAGES.map((lang) => {
        const isActive = lang.id === active;
        const isPending = pending === lang.id;
        return (
          <button
            key={lang.id}
            type="button"
            // Marks the SPAN of text as being in that language so a screen
            // reader pronounces "Қазақша" with Kazakh phonetics, not English.
            lang={lang.id}
            onClick={() => select(lang.id)}
            disabled={Boolean(pending)}
            aria-pressed={isActive}
            // The visible face is a 3-letter code; the accessible name is the
            // full endonym, so the control is not read out as "KAZ RUS ENG".
            aria-label={lang.name}
            title={lang.name}
            className={`rounded-md px-2.5 py-1 text-[11px] font-bold tracking-wider transition
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1
              disabled:cursor-wait ${
                isActive
                  ? "bg-white text-indigo-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              } ${isPending ? "opacity-100" : pending ? "opacity-40" : ""}`}
          >
            {lang.code}
          </button>
        );
      })}
    </div>
  );
}
